import type { GuidanceCode } from "./types";
import type { InternalQualityPolicy } from "./policy";
import { captureGuideFor, type CaptureMode } from "./target";

export interface InternalFrameAnalysis {
  passed: boolean;
  guidance: GuidanceCode;
  score: number;
  luminanceSample: Uint8Array;
  metrics: {
    coverage: number;
    centerOffset: number;
    sharpness: number;
    contrast: number;
    exposure: number;
    glare: number;
    motion: number;
    ridgeClarity: number;
    fingerSharpness: number[];
    fingerRidgeClarity: number[];
  };
}

interface GuideRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface DetailCell {
  laplacianSquared: number;
  gradientSum: number;
  count: number;
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const average = (values: number[]): number =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : 0;

function guideRect(
  width: number,
  height: number,
  captureMode: CaptureMode,
): GuideRect {
  const guide = captureGuideFor(captureMode);

  return {
    left: Math.floor(width * guide.left),
    top: Math.floor(height * guide.top),
    right: Math.ceil(width * (guide.left + guide.width)),
    bottom: Math.ceil(height * (guide.top + guide.height)),
  };
}

function isProbableSkin(r: number, g: number, b: number): boolean {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const y = 0.299 * r + 0.587 * g + 0.114 * b;
  const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
  const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;

  const rgbRule =
    r > 55 &&
    g > 30 &&
    b > 18 &&
    max - min > 10 &&
    r >= g * 0.82 &&
    r > b * 0.92;
  const chromaRule = cb >= 72 && cb <= 142 && cr >= 125 && cr <= 184;

  return y > 28 && y < 248 && (rgbRule || chromaRule);
}

function createLuminanceSample(
  luma: Uint8Array,
  width: number,
  height: number,
): Uint8Array {
  const sampleWidth = 32;
  const sampleHeight = 40;
  const sample = new Uint8Array(sampleWidth * sampleHeight);

  for (let sy = 0; sy < sampleHeight; sy += 1) {
    const y = Math.min(height - 1, Math.floor((sy / sampleHeight) * height));
    for (let sx = 0; sx < sampleWidth; sx += 1) {
      const x = Math.min(width - 1, Math.floor((sx / sampleWidth) * width));
      sample[sy * sampleWidth + sx] = luma[y * width + x];
    }
  }

  return sample;
}

function calculateMotion(
  current: Uint8Array,
  previous?: Uint8Array,
): number {
  if (!previous || previous.length !== current.length) return 1;

  let difference = 0;
  for (let index = 0; index < current.length; index += 1) {
    difference += Math.abs(current[index] - previous[index]);
  }

  return clamp01(difference / current.length / 42);
}

function detailValue(cell: DetailCell): { sharpness: number; ridges: number } {
  if (cell.count === 0) return { sharpness: 0, ridges: 0 };
  return {
    sharpness: clamp01(cell.laplacianSquared / cell.count / 1200),
    ridges: clamp01((cell.gradientSum / cell.count - 4) / 35),
  };
}

export function analyzeFrame(
  image: ImageData,
  policy: InternalQualityPolicy,
  previousLuminance?: Uint8Array,
  captureMode: CaptureMode = "single-finger",
  measureDetail = true,
): InternalFrameAnalysis {
  const { width, height, data } = image;
  const luma = new Uint8Array(width * height);
  const skinMask = new Uint8Array(width * height);
  const guide = guideRect(width, height, captureMode);

  let skinCount = 0;
  let sumX = 0;
  let sumY = 0;
  let lumaSum = 0;
  let lumaSquaredSum = 0;
  let darkCount = 0;
  let brightCount = 0;
  let glareCount = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixel = y * width + x;
      const offset = pixel * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const value = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      luma[pixel] = value;

      if (
        x < guide.left ||
        x >= guide.right ||
        y < guide.top ||
        y >= guide.bottom ||
        !isProbableSkin(r, g, b)
      ) {
        continue;
      }

      skinMask[pixel] = 1;
      skinCount += 1;
      sumX += x;
      sumY += y;
      lumaSum += value;
      lumaSquaredSum += value * value;
      if (value < 48) darkCount += 1;
      if (value > 228) brightCount += 1;
      if (value > 215 && Math.max(r, g, b) - Math.min(r, g, b) < 24) {
        glareCount += 1;
      }
    }
  }

  const guideWidth = guide.right - guide.left;
  const guideHeight = guide.bottom - guide.top;
  const guidePixels = guideWidth * guideHeight;
  const coverage = guidePixels > 0 ? skinCount / guidePixels : 0;
  const mean = skinCount > 0 ? lumaSum / skinCount : 0;
  const variance =
    skinCount > 0
      ? Math.max(0, lumaSquaredSum / skinCount - mean * mean)
      : 0;
  const contrast = clamp01(Math.sqrt(variance) / 58);
  const exposure =
    skinCount > 0
      ? clamp01(1 - (darkCount + brightCount) / skinCount / 0.42)
      : 0;
  const glare = skinCount > 0 ? glareCount / skinCount : 1;

  const centerX = skinCount > 0 ? sumX / skinCount : 0;
  const centerY = skinCount > 0 ? sumY / skinCount : 0;
  const guideCenterX = (guide.left + guide.right) / 2;
  const guideCenterY = (guide.top + guide.bottom) / 2;
  const normalizedX = Math.abs(centerX - guideCenterX) / (guideWidth / 2);
  const normalizedY = Math.abs(centerY - guideCenterY) / (guideHeight / 2);
  const centerOffset = clamp01(Math.hypot(normalizedX, normalizedY) / 1.4);

  let laplacianSquared = 0;
  let gradientSum = 0;
  let gradientCount = 0;
  const cells: DetailCell[] = Array.from({ length: 16 }, () => ({
    laplacianSquared: 0,
    gradientSum: 0,
    count: 0,
  }));

  if (measureDetail) {
    for (let y = guide.top + 1; y < guide.bottom - 1; y += 2) {
      for (let x = guide.left + 1; x < guide.right - 1; x += 2) {
        const pixel = y * width + x;
        if (!skinMask[pixel]) continue;

        const center = luma[pixel];
        const laplacian =
          luma[pixel - 1] +
          luma[pixel + 1] +
          luma[pixel - width] +
          luma[pixel + width] -
          center * 4;
        const gx = luma[pixel + 1] - luma[pixel - 1];
        const gy = luma[pixel + width] - luma[pixel - width];
        const laplacianValue = laplacian * laplacian;
        const gradientValue = Math.hypot(gx, gy);

        laplacianSquared += laplacianValue;
        gradientSum += gradientValue;
        gradientCount += 1;

        if (captureMode === "four-fingers") {
          const row = Math.min(
            3,
            Math.floor(((y - guide.top) / guideHeight) * 4),
          );
          const column = Math.min(
            3,
            Math.floor(((x - guide.left) / guideWidth) * 4),
          );
          const cell = cells[row * 4 + column];
          cell.laplacianSquared += laplacianValue;
          cell.gradientSum += gradientValue;
          cell.count += 1;
        }
      }
    }
  }

  const globalDetail = detailValue({
    laplacianSquared,
    gradientSum,
    count: gradientCount,
  });
  const fingerSharpness: number[] = [];
  const fingerRidgeClarity: number[] = [];

  if (measureDetail && captureMode === "four-fingers") {
    for (let row = 0; row < 4; row += 1) {
      let best = { sharpness: 0, ridges: 0, score: 0 };
      for (let column = 0; column < 4; column += 1) {
        const cell = cells[row * 4 + column];
        if (cell.count < 12) continue;
        const detail = detailValue(cell);
        const score = detail.sharpness * 0.65 + detail.ridges * 0.35;
        if (score > best.score) best = { ...detail, score };
      }
      fingerSharpness.push(best.sharpness);
      fingerRidgeClarity.push(best.ridges);
    }
  }

  const sharpness =
    fingerSharpness.length === 4
      ? average(fingerSharpness)
      : globalDetail.sharpness;
  const ridgeClarity =
    fingerRidgeClarity.length === 4
      ? average(fingerRidgeClarity)
      : globalDetail.ridges;
  const luminanceSample = createLuminanceSample(luma, width, height);
  const motion = calculateMotion(luminanceSample, previousLuminance);

  let guidance: GuidanceCode = "READY";
  if (coverage < 0.08) guidance = "PLACE_FINGER";
  else if (coverage < policy.minimumCoverage) guidance = "MOVE_CLOSER";
  else if (coverage > policy.maximumCoverage) guidance = "MOVE_BACK";
  else if (centerOffset > policy.maximumCenterOffset)
    guidance = "CENTER_FINGER";
  else if (mean < 65 || exposure < policy.minimumExposure)
    guidance = mean < 105 ? "TOO_DARK" : "TOO_BRIGHT";
  else if (glare > policy.maximumGlare) guidance = "REDUCE_GLARE";
  else if (motion > policy.maximumMotion) guidance = "HOLD_STILL";
  else if (measureDetail && sharpness < policy.minimumSharpness)
    guidance = "IMAGE_BLURRY";
  else if (
    measureDetail &&
    (contrast < policy.minimumContrast ||
      ridgeClarity < policy.minimumRidgeClarity)
  )
    guidance = "RIDGES_NOT_VISIBLE";

  const passed = guidance === "READY";
  const score = clamp01(
    coverage * 0.12 +
      (1 - centerOffset) * 0.13 +
      sharpness * 0.22 +
      contrast * 0.13 +
      exposure * 0.12 +
      (1 - glare) * 0.08 +
      (1 - motion) * 0.08 +
      ridgeClarity * 0.12,
  );

  return {
    passed,
    guidance,
    score,
    luminanceSample,
    metrics: {
      coverage,
      centerOffset,
      sharpness,
      contrast,
      exposure,
      glare,
      motion,
      ridgeClarity,
      fingerSharpness,
      fingerRidgeClarity,
    },
  };
}
